import React, { useState, useEffect } from 'react';
import { 
  ClockIcon,
  TrashIcon,
  PlayIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { 
  ApiRequest, 
  getRequestHistory, 
  clearRequestHistory 
} from '../../services/apiPlaygroundService';

interface RequestHistoryProps {
  onRequestSelect: (request: ApiRequest) => void;
}

const RequestHistory: React.FC<RequestHistoryProps> = ({ onRequestSelect }) => {
  const [history, setHistory] = useState<Array<{
    id: string;
    timestamp: string;
    request: ApiRequest;
    response: any;
  }>>([]);
  const [expandedItem, setExpandedItem] = useState<string | null>(null);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = () => {
    const historyData = getRequestHistory();
    setHistory(historyData);
  };

  const handleClearHistory = () => {
    if (window.confirm('Are you sure you want to clear all request history?')) {
      clearRequestHistory();
      setHistory([]);
    }
  };

  const getStatusColor = (status: number) => {
    if (status >= 200 && status < 300) return 'text-green-600';
    if (status >= 400 && status < 500) return 'text-yellow-600';
    if (status >= 500) return 'text-red-600';
    return 'text-gray-600';
  };

  const getStatusIcon = (status: number) => {
    if (status >= 200 && status < 300) return CheckCircleIcon;
    if (status >= 400 && status < 500) return ExclamationCircleIcon;
    if (status >= 500) return XCircleIcon;
    return ClockIcon;
  };

  const getMethodColor = (method: string) => {
    switch (method.toUpperCase()) {
      case 'GET': return 'bg-green-100 text-green-800';
      case 'POST': return 'bg-blue-100 text-blue-800';
      case 'PUT': return 'bg-yellow-100 text-yellow-800';
      case 'DELETE': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const formatResponseTime = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const getUrlPath = (url: string) => {
    try {
      const urlObj = new URL(url);
      return urlObj.pathname;
    } catch {
      return url;
    }
  };

  if (history.length === 0) {
    return (
      <div className="text-center py-12">
        <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No request history</h3>
        <p className="mt-1 text-sm text-gray-500">
          Execute some API requests to see them here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Request History</h3>
          <p className="text-sm text-gray-500">
            {history.length} request{history.length !== 1 ? 's' : ''} in history
          </p>
        </div>
        {history.length > 0 && (
          <button
            onClick={handleClearHistory}
            className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <TrashIcon className="h-4 w-4 mr-1" />
            Clear History
          </button>
        )}
      </div>

      {/* History List */}
      <div className="space-y-2">
        {history.map((item) => {
          const isExpanded = expandedItem === item.id;
          const StatusIcon = getStatusIcon(item.response.status);
          
          return (
            <div
              key={item.id}
              className="bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow"
            >
              {/* Item Header */}
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div className="flex-shrink-0">
                      <StatusIcon className={`h-5 w-5 ${getStatusColor(item.response.status)}`} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getMethodColor(item.request.method)}`}>
                          {item.request.method}
                        </span>
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {getUrlPath(item.request.url)}
                        </span>
                        <span className={`text-sm font-medium ${getStatusColor(item.response.status)}`}>
                          {item.response.status}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span>{formatTimestamp(item.timestamp)}</span>
                        <span>{formatResponseTime(item.response.responseTime)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => onRequestSelect(item.request)}
                      className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs leading-4 font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <PlayIcon className="h-3 w-3 mr-1" />
                      Use
                    </button>
                    <button
                      onClick={() => setExpandedItem(isExpanded ? null : item.id)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      <ChevronRightIcon 
                        className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} 
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="border-t border-gray-200 bg-gray-50">
                  <div className="p-4 space-y-4">
                    {/* Request Details */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Request</h4>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="font-medium text-gray-700">URL:</span>
                          <span className="ml-2 text-gray-900 break-all">{item.request.url}</span>
                        </div>
                        
                        {Object.keys(item.request.headers).length > 0 && (
                          <div>
                            <span className="font-medium text-gray-700">Headers:</span>
                            <div className="ml-2 mt-1 space-y-1">
                              {Object.entries(item.request.headers).map(([key, value]) => (
                                <div key={key} className="text-xs">
                                  <span className="text-gray-600">{key}:</span>
                                  <span className="ml-1 text-gray-800">{value}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {item.request.body && (
                          <div>
                            <span className="font-medium text-gray-700">Body:</span>
                            <pre className="ml-2 mt-1 text-xs bg-white border border-gray-200 rounded p-2 overflow-x-auto">
                              {item.request.body}
                            </pre>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Response Summary */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Response Summary</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-700">Status:</span>
                          <span className={`ml-2 ${getStatusColor(item.response.status)}`}>
                            {item.response.status} {item.response.statusText}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Response Time:</span>
                          <span className="ml-2 text-gray-900">
                            {formatResponseTime(item.response.responseTime)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RequestHistory;
